/* P.Louise — Product page information
   Lightweight accordion for the standalone product information section,
   with an Ingredients panel that follows the selected variant.

   The section carries its own per-variant ingredient bank (pulled from the
   variant metafield my_fields.ingredients) and resolves the current variant
   from several signals so it works regardless of which selector the product
   page uses:
     1. A generic `variant:change` CustomEvent (detail.variantId).
     2. The `?variant=` URL parameter, which Shopify themes keep in sync on
        variant change (covered via history patch + popstate).
     3. A native variant `<select|input name="id">` change event. */
(function () {
  'use strict';

  /* ---- Broadcast a single signal whenever the URL changes, including the
     history.replaceState/pushState calls that themes use on variant select
     (these do not fire popstate on their own). Patched once per page. ---- */
  function patchHistory() {
    if (window.__ppiHistoryPatched) return;
    window.__ppiHistoryPatched = true;
    ['pushState', 'replaceState'].forEach(function (name) {
      var original = history[name];
      if (typeof original !== 'function') return;
      history[name] = function () {
        var result = original.apply(this, arguments);
        try {
          window.dispatchEvent(new Event('ppi:locationchange'));
        } catch (e) { /* ignore */ }
        return result;
      };
    });
    window.addEventListener('popstate', function () {
      try {
        window.dispatchEvent(new Event('ppi:locationchange'));
      } catch (e) { /* ignore */ }
    });
  }

  function variantFromUrl() {
    try {
      return new URLSearchParams(window.location.search).get('variant');
    } catch (e) {
      return null;
    }
  }

  function initAccordion(root) {
    var triggers = root.querySelectorAll('[data-ppi-trigger]');
    Array.prototype.forEach.call(triggers, function (trigger) {
      trigger.addEventListener('click', function () {
        var panel = trigger.nextElementSibling;
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) {
          panel.classList.toggle('is-open', !expanded);
        }
      });
    });
  }

  function initIngredients(root) {
    var content = root.querySelector('[data-ppi-ingredients]');
    if (!content) return;

    var templates = {};
    var nodes = root.querySelectorAll('[data-ppi-ingredients-data] template');
    Array.prototype.forEach.call(nodes, function (tpl) {
      templates[String(tpl.getAttribute('data-variant-id'))] = tpl.innerHTML;
    });

    var current = null;
    function swap(variantId) {
      if (variantId === null || variantId === undefined || variantId === '') return;
      var key = String(variantId);
      if (key === current) return;
      var html = templates[key];
      if (html !== undefined) {
        content.innerHTML = html;
        current = key;
      }
    }

    /* Pull a variant id out of an event detail, whatever shape it takes. */
    function idFromDetail(detail) {
      if (!detail) return null;
      if (detail.variantId != null) return detail.variantId;
      if (detail.variant && detail.variant.id != null) return detail.variant.id;
      if (detail.id != null) return detail.id;
      return null;
    }

    /* Initial state: variant from the URL, else the server-rendered default. */
    swap(variantFromUrl());

    /* 1. Theme / custom variant-change events. The Fluorescent (Cornerstone)
       theme this store runs emits `product:variant-change` with
       detail.variant; the others cover generic and other-theme conventions. */
    ['product:variant-change', 'variant:change', 'variantChange', 'on:variant:change'].forEach(
      function (name) {
        document.addEventListener(name, function (event) {
          swap(idFromDetail(event.detail));
        });
      }
    );

    /* 2. URL `?variant=` sync — themes update this on variant change (covered
       via the history patch + popstate above), so this works even when the
       theme's custom events are turned off. */
    window.addEventListener('ppi:locationchange', function () {
      swap(variantFromUrl());
    });

    /* 3. Native variant selector input whose value is the variant id. */
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.name === 'id' && t.value) swap(t.value);
    });
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-product-page-information]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__ppiInitialised) return;
      root.__ppiInitialised = true;
      initAccordion(root);
      initIngredients(root);
    });
  }

  patchHistory();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Re-initialise when the section is (re)loaded in the Shopify theme editor. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();